// Route guard. If /api/me has not yet resolved, render a loader; if it
// resolved to anonymous, redirect to /login. Single probe per app load —
// no global state library.

import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader } from '@progress/kendo-react-indicators';
import { useAuth } from '../lib/auth';

interface Props {
  children: ReactNode;
}

export function RequireAuth({ children }: Props) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === 'unknown') {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <Loader size="large" type="converging-spinner" />
      </div>
    );
  }
  if (state.status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // MFA enforcement gate: an authenticated-but-MFA-pending session (enrolled
  // user who hasn't entered a code, or an unenrolled user in an MFA-required
  // tenant) is routed to the challenge before the shell — and its live hub
  // providers — mount. /mfa-required is a top-level route outside this guard.
  if (!state.user.mfaSatisfied) {
    return <Navigate to="/mfa-required" replace state={{ from: location }} />;
  }
  return <>{children}</>;
}
